// Typed client for /api/v1/passes/* (ONDC TRV14 unreserved entry passes — events,
// museums, venues) over the shared authenticated fetch core (lib/http.ts). Like
// ticketsApi it has a Razorpay payment step (collected by BAP), plus a category-based
// discover and an xinput additional-details form (forms/{txn} + forms/submit).
import { request, q } from './http';
import type {
  PassDiscoverResponse,
  PassDraft,
  PassTicket,
  Form,
  PassContact,
  PassPaymentOrderResponse,
  PassSearchRequest,
  PassSelectRequest,
  PassSubmitFormRequest,
  PassInitRequest,
  PassConfirmRequest,
  PassCancelRequest,
  PassRateRequest,
  PassRaiseIssueRequest,
  PassTransactionAck,
  PassSubmitFormResponse,
  ListPassesResponse,
  PassIssue,
  PassListIssuesResponse,
  PassIssueAck,
} from './passes-types';

/** Build a `?a=1&b=2` query string from defined, non-empty params. */
function query(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${q(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const passesApi = {
  // Category-based discovery (returns {status, transactionId?, venues?} — poll the
  // draft when status === 'discovering').
  discover: (params: { city: string; category?: string; gps?: string; date?: string }) =>
    request<PassDiscoverResponse>(
      'GET',
      `/api/v1/passes/discover${query({
        city: params.city,
        pass_category: params.category,
        gps: params.gps,
        date: params.date,
      })}`
    ),

  // Booking flow (async draft, polled via usePassDraft → useBeckonDraft).
  search: (body: PassSearchRequest) =>
    request<PassTransactionAck>('POST', '/api/v1/passes/search', body),
  draft: (txnId: string) => request<PassDraft>('GET', `/api/v1/passes/transactions/${q(txnId)}`),
  select: (body: PassSelectRequest) =>
    request<PassTransactionAck>('POST', '/api/v1/passes/select', body),

  // xinput additional-details form (BPP-hosted; the user fills it at form.url and we
  // record + echo the returned submissionId).
  form: (txnId: string) => request<Form>('GET', `/api/v1/passes/forms/${q(txnId)}`),
  submitForm: (body: PassSubmitFormRequest) =>
    request<PassSubmitFormResponse>('POST', '/api/v1/passes/forms/submit', body),

  init: (body: PassInitRequest) => request<PassTransactionAck>('POST', '/api/v1/passes/init', body),
  paymentOrder: (transactionId: string) =>
    request<PassPaymentOrderResponse>('POST', '/api/v1/passes/payment-order', { transactionId }),
  confirm: (body: PassConfirmRequest) =>
    request<PassTransactionAck>('POST', '/api/v1/passes/confirm', body),
  // Forces a fresh on_status round-trip from the BPP, then the order snapshot updates.
  status: (transactionId: string, orderId?: string) =>
    request<PassTransactionAck>('POST', '/api/v1/passes/status', { transactionId, orderId }),

  // Passes (post-purchase).
  list: (limit = 20, offset = 0) =>
    request<ListPassesResponse>('GET', `/api/v1/passes/list?limit=${limit}&offset=${offset}`),
  pass: (id: string) => request<PassTicket>('GET', `/api/v1/passes/${q(id)}`),
  cancel: (id: string, body: PassCancelRequest) =>
    request<PassTransactionAck>('POST', `/api/v1/passes/${q(id)}/cancel`, body),
  rate: (id: string, body: PassRateRequest) =>
    request<PassTransactionAck>('POST', `/api/v1/passes/${q(id)}/rate`, body),
  support: (id: string) => request<PassContact>('GET', `/api/v1/passes/${q(id)}/support`),

  // IGM (issue & grievance management) — mirrors ticketsApi/ridesApi.
  raiseIssue: (id: string, body: PassRaiseIssueRequest) =>
    request<PassIssueAck>('POST', `/api/v1/passes/${q(id)}/issues`, body),
  listAllIssues: (limit = 50, offset = 0) =>
    request<PassListIssuesResponse>('GET', `/api/v1/passes/issues?limit=${limit}&offset=${offset}`),
  listIssues: (id: string) =>
    request<PassListIssuesResponse>('GET', `/api/v1/passes/${q(id)}/issues`),
  issue: (issueId: string) => request<PassIssue>('GET', `/api/v1/passes/issues/${q(issueId)}`),
  updateIssue: (issueId: string, body: { actionCode?: string; shortDesc?: string }) =>
    request<PassIssueAck>('POST', `/api/v1/passes/issues/${q(issueId)}/update`, body),
  escalateIssue: (issueId: string, body: { shortDesc?: string }) =>
    request<PassIssueAck>('POST', `/api/v1/passes/issues/${q(issueId)}/escalate`, body),
  closeIssue: (issueId: string, body: { rating?: string }) =>
    request<PassIssueAck>('POST', `/api/v1/passes/issues/${q(issueId)}/close`, body),
};
